import { GetStaticProps } from "next";
import {
  AnalyticsContentProperties,
  SetAnalyticsDataForContent,
  apiV1ToAnalyticsContentProperties,
} from "~/components/Analytics";
import {
  ArticlePage,
  ArticlePageProps as ArticlePageComponentProps,
} from "~/components/ArticlePage/ArticlePage";
import { apiToAssetDownloadV1Props } from "~/components/AssetDownload";
import { postsToContentPromoProps } from "~/components/ContentPromo/postToContentPromoProps";
import {
  PageMeta,
  PostMetadata,
  apiPostToPageMetadata,
} from "~/components/PageMeta";
import { apiToPostEditCTAProps } from "~/components/PostEditCTA/apiToPostEditCTAProps";
import { fetchArticle, fetchArticlePreview } from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { fetchFeatureFlags } from "~/lib/server/featureFlags";
import { postToArticlePageProps } from "~/lib/server/postToArticlePageProps";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { PostTypeEnum } from "~/types/asgard/PostType";
import { NextPageWithLayout } from "~/types/endil/NextPageWithLayout";

export type ArticlePageParams = {
  articleParam: string;
};

export type ArticlePageProps = ArticlePageComponentProps & {
  meta: PostMetadata;
  analytics: AnalyticsContentProperties;
};

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.Article,
  PostTypeEnum.Article,
  "slug"
);

export const getStaticProps: GetStaticProps<
  ArticlePageProps,
  ArticlePageParams
> = wrapGetStaticProps(async ({ params, preview }) => {
  const { articleParam } = params!;
  const fetchMethod = preview ? fetchArticlePreview : fetchArticle;
  const { data: article } = await fetchMethod(articleParam);
  const postEditCTAProps = await apiToPostEditCTAProps({
    isPreview: preview,
    postId: article.id,
    postParam: articleParam,
  });
  const { enableSidecar } = await fetchFeatureFlags();
  return {
    props: {
      ...postToArticlePageProps({
        enableSidecar: preview || enableSidecar,
        post: article,
        preview,
      }),
      postEditCTAProps,
      meta: apiPostToPageMetadata(article),
      assetDownloadProps: apiToAssetDownloadV1Props({
        downloadableAssets: article.acf.downloadable_assets,
        postId: article.id,
        postName: article.title.rendered,
        assetSummary: article.acf.asset_summary,
      }),
      contentPromoProps: postsToContentPromoProps(article.relatedStories),
      analytics: apiV1ToAnalyticsContentProperties(article),
    } satisfies ArticlePageProps,
  };
});

const ArticlesPage: NextPageWithLayout<ArticlePageProps> = ({
  analytics,
  meta,
  ...props
}) => (
  <>
    <SetAnalyticsDataForContent {...analytics} />
    <PageMeta {...meta} />
    <ArticlePage {...props} />
  </>
);

export default ArticlesPage;
